import React from 'react';
import BrandLoader from '../../shared/brand/BrandLoader';
import GlassCard from '../../shared/brand/GlassCard';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatRelativeTime } from '../../shared/feed/feedTime';
import { useLicenseGate } from '../../../hooks/useLicenseGate';
import { DashboardProcessingItem } from '../../../store/reducer/DashboardReducer';
import {
  PiBriefcaseLight,
  PiFileMagnifyingGlassLight,
} from 'react-icons/pi';

interface ProcessingItemsProps {
  items: DashboardProcessingItem[];
}

const typeConfig: Record<string, { icon: React.ReactNode; route: string; color: string; bgColor: string }> = {
  matter: {
    icon: <PiBriefcaseLight className="w-4 h-4" />,
    route: '/matters',
    color: 'text-info-600 dark:text-info-400',
    bgColor: 'bg-info-50 dark:bg-info-900/30',
  },
  review: {
    icon: <PiFileMagnifyingGlassLight className="w-4 h-4" />,
    route: '/reviews',
    color: 'text-warning-600 dark:text-warning-400',
    bgColor: 'bg-warning-50 dark:bg-warning-900/30',
  },
};


/**
 * The items this user can actually open.
 *
 * Exported for the same reason as `accessibleTools`: the widget registry needs
 * the identical predicate to decide whether the widget exists at all.
 */
export const accessibleProcessingItems = (
  items: DashboardProcessingItem[],
  hasSectionAccess: (section: string) => boolean,
): DashboardProcessingItem[] => items.filter((item) => {
  if (item.type === 'matter') return hasSectionAccess('matters');
  if (item.type === 'review') return hasSectionAccess('review');
  return true;
});

const ProcessingItems: React.FC<ProcessingItemsProps> = ({ items }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { hasSectionAccess } = useLicenseGate();

  const visibleItems = accessibleProcessingItems(items, hasSectionAccess);

  if (visibleItems.length === 0) return null;

  return (
    <GlassCard
      icon={<BrandLoader size="xs" className="!h-4 !w-4" />}
      title={
        <>
          {t('Currently Processing')}
          <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs rounded-full bg-info-100 text-info-700 dark:bg-info-900/40 dark:text-info-300">
            {visibleItems.length}
          </span>
        </>
      }
      padding="none"
      className="overflow-hidden"
    >
      {/* Container-measured, like every other tile grid on the page. */}
      <div className="widget-metrics widget-metrics-3 gap-3 p-4">
        {visibleItems.map((item) => {
          const config = typeConfig[item.type] || typeConfig.matter;
          return (
            <div
              key={`${item.type}-${item.uuid}`}
              className="flex items-center gap-3 p-3.5 rounded-xl bg-surface-sunken/70 border border-hairline-soft
                hover:border-hairline hover:shadow-sm cursor-pointer transition-all"
              onClick={() => {
                if (item.uuid) history.push(`${config.route}/${item.uuid}`);
              }}
            >
              <div className={`flex-shrink-0 w-8 h-8 rounded-lg ${config.bgColor} ${config.color} flex items-center justify-center`}>
                {config.icon}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900 dark:text-gray-100 truncate">
                  {item.title}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                  {formatRelativeTime(item.started_at || undefined, t)}
                </p>
              </div>
              <div className="flex-shrink-0">
                <BrandLoader size="xs" className="!h-5 !w-5" />
              </div>
            </div>
          );
        })}
      </div>
    </GlassCard>
  );
};

export default ProcessingItems;
